import { Injectable } from '@angular/core';
import { ImmutableContext } from '@ngxs-labs/immer-adapter';
import { Action, Actions, Selector, State, StateContext, Store } from '@ngxs/store';
import { Chance } from 'chance';
import produce from 'immer';
import _ from 'lodash';
import move from 'lodash-move';
import { Observable } from 'rxjs';
import { BrickSettingsService, TabBrickSettingRemoteDto, TabContentRemoteDto } from '../../../../../../../generated/swagger';
import { PresentationType } from '../../../../../../samwebDescription/samweb/settings/presentationType';
import { TabPositionType } from '../../../../../../samwebDescription/samweb/settings/tabBrick/tabPositionType';
import { LayoutBrickAddedToWall } from '../../../brick-instances/brick-instances.actions';
import { BrickInstancesSelectors } from '../../../brick-instances/brick-instances.selectors';
import { IBrickDescription } from '../../../brick-instances/brick-instances.state';
import { IBrickSettingStateModel, SpecificBrickSettingsModelContainer, SpecificBrickSettingsStateBase } from '../specific-brick-settings-base';
import {
   ChangeBrickDefinitionMenuOpeningStateForBrickTab,
   ChangeBrickPosition,
   ChangeTabColorForTabBrick,
   ChangeTabIconForTabBrick,
   ChangeTabOrderForTabBrick,
   ChangeTabPresentation,
   ChangeTabTitleForTabBrick,
   RemoveTabForTabBrick,
   ToggleBrickDefinitionMenuOpeningStateForBrickTab,
} from './tab-brick-settings.actions';

export interface TabBrickSettingsStateModel extends IBrickSettingStateModel<TabBrickSettingRemoteDto> {
   isBrickDefinitionMenuOpen: boolean;
}

function getDefaultSpecificBrickSetting(): TabBrickSettingRemoteDto {
   return {
      tabPosition: TabPositionType.TOP.value,
      commonBrickSettingRemoteDto: null,
      tabs: [],
      tabPresentationType: PresentationType.ICON_AND_TEXT.value,
   };
}

@State<TabBrickSettingsStateModel>({
   name: 'tabBrickSettings',
   defaults: {
      specificBrickSettings: {
         brickSettings: {},
         brickSettingsEditMode: null,
      },
      isBrickDefinitionMenuOpen: false,
   },
})
@Injectable()
export class TabBrickSettingsState extends SpecificBrickSettingsStateBase<TabBrickSettingRemoteDto>(getDefaultSpecificBrickSetting) {
   private chance = new Chance();

   constructor(private brickSettingsService: BrickSettingsService, store: Store, actions$: Actions) {
      super(store);
   }

   @Selector([TabBrickSettingsState])
   public static settingsFromServerReceived(state: TabBrickSettingsStateModel): boolean {
      return state.settingsFromServerReceived;
   }

   @Selector([TabBrickSettingsState])
   public static isBrickDefinitionMenuOpen(state: TabBrickSettingsStateModel): boolean {
      return state.isBrickDefinitionMenuOpen;
   }

   @Selector([TabBrickSettingsState.getBrickInstance])
   public static getTabs(getBrickInstance: (brickId: string) => TabBrickSettingRemoteDto) {
      return (brickInstanceId: string) => {
         return getBrickInstance(brickInstanceId).tabs;
      };
   }

   @Selector([TabBrickSettingsState.getBrickInstance])
   public static getTabPosition(getBrickInstance: (brickId: string) => TabBrickSettingRemoteDto) {
      return (brickInstanceId: string) => {
         return getBrickInstance(brickInstanceId).tabPosition;
      };
   }

   @Selector([TabBrickSettingsState.getBrickInstance])
   public static getTabPresentationType(getBrickInstance: (brickId: string) => TabBrickSettingRemoteDto) {
      return (brickInstanceId: string) => {
         return getBrickInstance(brickInstanceId).tabPresentationType;
      };
   }

   @Selector([TabBrickSettingsState.getTabPosition])
   public static getTabPositionType(getTabPosition: (brickInstanceId: string) => string) {
      return (brickInstanceId: string) => {
         return TabPositionType.valueOf(getTabPosition(brickInstanceId));
      };
   }

   @Selector([TabBrickSettingsState.getTabPresentationType])
   public static getPresentationType(getTabPresentationType: (brickInstanceId: string) => string) {
      return (brickInstanceId: string) => {
         return PresentationType.valueOf(getTabPresentationType(brickInstanceId));
      };
   }

   @Selector([TabBrickSettingsState.getTabs])
   public static getTabContentForBrickInstanceIdFn(getTabs: (brickInstanceId) => Array<TabContentRemoteDto>) {
      return (brickInstanceId: string, tabId: string) => {
         return getTabs(brickInstanceId).find((x) => x.tabId === tabId);
      };
   }

   @Selector([TabBrickSettingsState.getTabs])
   public static getTabIndex(getTabsInOrder: (brickInstanceId) => Array<TabContentRemoteDto>) {
      return (brickInstanceId: string, tabId: string) => {
         return _.findIndex(getTabsInOrder(brickInstanceId), (x) => x.tabId === tabId);
      };
   }

   @Selector([TabBrickSettingsState.currentBrickSettings])
   public static getIsBrickInstanceHostingChildBrick(settings: {
      [p: string]: TabBrickSettingRemoteDto;
   }): (hostingBrickId: string, targetBrickId: string) => boolean {
      return (hostingBrickId: string, targetBrickId: string, deepCheckFn: (hostingBrickId: string, targetBrickId: string, deepCheck) => boolean = null) => {
         if (!_.has(settings, hostingBrickId)) {
            return false;
         }
         const isTargetBrickPresent = settings[hostingBrickId].tabs.some((x) => x.brickInstanceId === targetBrickId);
         if (isTargetBrickPresent || _.isNil(deepCheckFn)) {
            return isTargetBrickPresent;
         }
         return settings[hostingBrickId].tabs.some((x) => deepCheckFn(x.brickInstanceId, targetBrickId, true));
      };
   }

   @Selector([TabBrickSettingsState.currentBrickSettings, TabBrickSettingsState.getIsCurrentSettingInstance])
   public static getIsBrickInstancePartOfAnyTabBrick(
      settings: { [p: string]: TabBrickSettingRemoteDto },
      getIsCurrentSettingInstance: (brickInstanceId: string, instance: TabBrickSettingRemoteDto) => boolean
   ): (brickInstanceId: string) => boolean {
      return (brickInstanceId: string) => {
         return _(settings)
            .values()
            .some((x: TabBrickSettingRemoteDto) => getIsCurrentSettingInstance(brickInstanceId, x));
      };
   }

   @Selector([TabBrickSettingsState.currentBrickSettings, TabBrickSettingsState.getIsCurrentSettingInstance])
   public static getBrickInstanceIdOfParent(
      settings: { [p: string]: TabBrickSettingRemoteDto },
      getIsCurrentSettingInstance: (brickInstanceId: string, instance: TabBrickSettingRemoteDto) => boolean
   ): (brickInstanceId: string) => string {
      return (brickInstanceId: string) => {
         return _.findKey(settings, (x: TabBrickSettingRemoteDto) => {
            return getIsCurrentSettingInstance(brickInstanceId, x);
         });
      };
   }

   @Selector()
   public static getIsCurrentSettingInstance() {
      return (brickInstanceId: string, instance: TabBrickSettingRemoteDto) => {
         return !_.isNil(instance) && !_.isNil(instance.tabs) && instance.tabs.some((tab) => tab.brickInstanceId === brickInstanceId);
      };
   }

   @Selector([TabBrickSettingsState, TabBrickSettingsState.getSettingModelFn])
   private static getBrickInstance(state: TabBrickSettingsState, getSettingModelFn: (state, brickInstanceId) => TabBrickSettingRemoteDto) {
      return (brickId: string) => {
         return getSettingModelFn(state, brickId);
      };
   }

   @Selector([TabBrickSettingsState])
   private static specificBrickSettings(state: TabBrickSettingsStateModel) {
      return state.specificBrickSettings;
   }

   @Selector([TabBrickSettingsState.specificBrickSettings])
   private static brickSettingsEditMode(specificBrickSettings: SpecificBrickSettingsModelContainer<TabBrickSettingRemoteDto>) {
      return specificBrickSettings.brickSettingsEditMode;
   }

   @Selector([TabBrickSettingsState.specificBrickSettings])
   private static brickSettings(specificBrickSettings: SpecificBrickSettingsModelContainer<TabBrickSettingRemoteDto>) {
      return specificBrickSettings.brickSettings;
   }

   @Selector([TabBrickSettingsState.brickSettings, TabBrickSettingsState.brickSettingsEditMode])
   private static currentBrickSettings(
      brickSettings: {
         [brickId: string]: TabBrickSettingRemoteDto;
      },
      brickSettingsEditMode: {
         [brickId: string]: TabBrickSettingRemoteDto;
      }
   ) {
      if (_.isNil(brickSettingsEditMode)) {
         return brickSettings;
      } else {
         return brickSettingsEditMode;
      }
   }

   protected doSaveSpecificSettings(brickInstanceId: string, brickSetting: TabBrickSettingRemoteDto): Observable<any> {
      return this.brickSettingsService.changeTabBrickSettings({
         brickInstanceId: brickInstanceId,
         settings: brickSetting,
      });
   }

   @Action(ChangeBrickDefinitionMenuOpeningStateForBrickTab)
   public changeBrickDefinitionMenuOpeningState(ctx: StateContext<TabBrickSettingsStateModel>, action: ChangeBrickDefinitionMenuOpeningStateForBrickTab) {
      ctx.patchState({
         isBrickDefinitionMenuOpen: action.isOpen,
      });
   }

   @Action(ToggleBrickDefinitionMenuOpeningStateForBrickTab)
   public toggleBrickDefinitionMenuOpeningState(ctx: StateContext<TabBrickSettingsStateModel>, action: ToggleBrickDefinitionMenuOpeningStateForBrickTab) {
      ctx.dispatch(
         new ChangeBrickDefinitionMenuOpeningStateForBrickTab(
            action.brickInstanceId,
            !this.store.selectSnapshot(TabBrickSettingsState.isBrickDefinitionMenuOpen)
         )
      );
   }

   @Action(ChangeTabOrderForTabBrick)
   public changeTabOrderForTabBrick(ctx: StateContext<TabBrickSettingsStateModel>, action: ChangeTabOrderForTabBrick) {
      const state = produce(ctx.getState(), (draft) => {
         const brickSettingModel = TabBrickSettingsState.getSettingModelOrDefault(draft, action.brickInstanceId);
         if (action.newIndex < 0 || action.newIndex > brickSettingModel.tabs.length - 1) {
            return;
         }
         const newOrderedItems = move(brickSettingModel.tabs, action.oldIndex, action.newIndex);
         brickSettingModel.tabs = newOrderedItems;
         draft.specificBrickSettings.brickSettingsEditMode[action.brickInstanceId] = brickSettingModel;
      });
      ctx.setState(state);
   }

   @Action(ChangeTabPresentation)
   public changeTabPresentation(ctx: StateContext<TabBrickSettingsStateModel>, action: ChangeTabPresentation) {
      const state = produce(ctx.getState(), (draft) => {
         const brickSettingModel = TabBrickSettingsState.getSettingModelOrDefault(draft, action.brickInstanceId);
         brickSettingModel.tabPresentationType = action.tabPresentationType.value;
         draft.specificBrickSettings.brickSettingsEditMode[action.brickInstanceId] = brickSettingModel;
      });
      ctx.setState(state);
   }

   @Action(ChangeBrickPosition)
   public changeBrickPosition(ctx: StateContext<TabBrickSettingsStateModel>, action: ChangeBrickPosition) {
      const state = produce(ctx.getState(), (draft) => {
         const brickSettingModel = TabBrickSettingsState.getSettingModelOrDefault(draft, action.brickInstanceId);
         brickSettingModel.tabPosition = action.tabPosition.value;
         draft.specificBrickSettings.brickSettingsEditMode[action.brickInstanceId] = brickSettingModel;
      });
      ctx.setState(state);
   }

   @Action(ChangeTabTitleForTabBrick)
   public changeTabTitleForTabBrick(ctx: StateContext<TabBrickSettingsStateModel>, action: ChangeTabTitleForTabBrick) {
      const state = produce(ctx.getState(), (draft) => {
         const brickSettingModel: TabBrickSettingRemoteDto = TabBrickSettingsState.getSettingModelOrDefault(draft, action.brickInstanceId);
         const tabContent = _.find(brickSettingModel.tabs, (x) => x.tabId === action.tabId);
         if (_.isNil(tabContent)) {
            return;
         }
         tabContent.tabTitle = action.newTitle;

         draft.specificBrickSettings.brickSettingsEditMode[action.brickInstanceId] = brickSettingModel;
      });
      ctx.setState(state);
   }

   @Action(ChangeTabColorForTabBrick)
   public changeTabColorForTabBrick(ctx: StateContext<TabBrickSettingsStateModel>, action: ChangeTabColorForTabBrick) {
      const state = produce(ctx.getState(), (draft) => {
         const brickSettingModel: TabBrickSettingRemoteDto = TabBrickSettingsState.getSettingModelOrDefault(draft, action.brickInstanceId);
         const tabContent = _.find(brickSettingModel.tabs, (tabC) => tabC.tabId === action.tabId);
         if (_.isNil(tabContent)) {
            return;
         }
         tabContent.tabColor = action.newColor;

         draft.specificBrickSettings.brickSettingsEditMode[action.brickInstanceId] = brickSettingModel;
      });
      ctx.setState(state);
   }

   @Action(ChangeTabIconForTabBrick)
   public changeTabIconForTabBrick(ctx: StateContext<TabBrickSettingsStateModel>, action: ChangeTabIconForTabBrick) {
      const state = produce(ctx.getState(), (draft) => {
         const brickSettingModel: TabBrickSettingRemoteDto = TabBrickSettingsState.getSettingModelOrDefault(draft, action.brickInstanceId);
         const tabContent = _.find(brickSettingModel.tabs, (x) => x.tabId === action.tabId);
         if (_.isNil(tabContent)) {
            return;
         }
         tabContent.tabIcon = action.tabIcon.iconName;

         draft.specificBrickSettings.brickSettingsEditMode[action.brickInstanceId] = brickSettingModel;
      });
      ctx.setState(state);
   }

   @Action(RemoveTabForTabBrick)
   public removeTabForTabBrick(ctx: StateContext<TabBrickSettingsStateModel>, action: RemoveTabForTabBrick) {
      const state = produce(ctx.getState(), (draft) => {
         const brickSettingModel: TabBrickSettingRemoteDto = TabBrickSettingsState.getSettingModelOrDefault(draft, action.brickInstanceId);
         if (brickSettingModel.tabs.length <= 1) {
            return;
         }
         _.remove(brickSettingModel.tabs, (x) => x.tabId === action.tabId);
         draft.specificBrickSettings.brickSettingsEditMode[action.brickInstanceId] = brickSettingModel;
      });
      ctx.setState(state);
   }

   @Action(LayoutBrickAddedToWall)
   @ImmutableContext()
   // this adds a new tab!
   public handleLayoutBrickAddedToWall(ctx: StateContext<TabBrickSettingsStateModel>, action: LayoutBrickAddedToWall) {
      if (
         this.store.selectSnapshot(BrickInstancesSelectors.brickDescriptionFn)(action.responsibleLayoutBrickInstanceId).definition.brickDefinitionId !==
         'tabBrick'
      ) {
         return;
      }
      ctx.setState((state: TabBrickSettingsStateModel) => {
         const brickSettingModel: TabBrickSettingRemoteDto = TabBrickSettingsState.getSettingModelOrDefault(state, action.responsibleLayoutBrickInstanceId);
         const brickDescription: IBrickDescription = this.store.selectSnapshot(BrickInstancesSelectors.brickDescriptionFn)(action.newBrickInstanceId);

         brickSettingModel.tabs.unshift({
            tabId: this.chance.string({ length: 5, casing: 'lower', alpha: true, numeric: true }),
            tabIcon: brickDescription.definition.brickIcon.iconName,
            brickInstanceId: brickDescription.brickInstance.brickInstanceId,
         });
         state.specificBrickSettings.brickSettingsEditMode[action.responsibleLayoutBrickInstanceId] = brickSettingModel;
         state.isBrickDefinitionMenuOpen = false;
         return state;
      });
   }
}
